import type { Placement } from '@floating-ui/react-dom';
import {
  autoUpdate,
  flip,
  hide,
  offset,
  useFloating,
} from '@floating-ui/react-dom';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import * as _ from 'lodash-es';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import React, {
  forwardRef,
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import * as styles from './EmojiPicker.module.scss';
import FormField from './formInternals/FormField';
import IconButton from './IconButton';
import ScrollArea from './ScrollArea';
import SVGIcon from './SVGIcon';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import activitiesIcon from '../icons/non-prefilled/emojis/activities.svg';
import animalsNatureIcon from '../icons/non-prefilled/emojis/animalsNature.svg';
import flagIcon from '../icons/non-prefilled/emojis/flag.svg';
import foodDrinksIcon from '../icons/non-prefilled/emojis/foodDrinks.svg';
import homeReactionIcon from '../icons/non-prefilled/emojis/homeReaction.svg';
import lightbulbIcon from '../icons/non-prefilled/emojis/lightbulb.svg';
import smileysPeopleIcon from '../icons/non-prefilled/emojis/smileysPeople.svg';
import travelPlacesIcon from '../icons/non-prefilled/emojis/travelPlaces.svg';
import EmojiComponent from '../post/comment/Emoji';
import { emojiSkinToneModifierAtom, useRecentlyUsedEmojis } from '../ui/atoms';
import addTestId from '../utils/addTestId';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import type { Emoji } from '../utils/emoji';
import {
  applySkinToneToEmoji,
  emojiList,
  getEmojiItemByChar,
  getEmojiItemByName,
  searchEmoji,
  skinToneModifiers,
} from '../utils/emoji';
import type { EmojiCategory as EmojiCategoryType } from '../utils/emojiData';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import useEventListener from '../utils/useEventListener';

const EMOJI_WIDTH = parseInt(styles.emojiWidth, 10);
const EMOJI_LIST_HORIZONTAL_PODDING = parseInt(
  styles.emojiListHorizontalPadding,
  10,
);
const TAB_LIST_HORIZONTAL_PADDING = parseInt(
  styles.tabListHorizontalPadding,
  10,
);
const EMOJI_INDEX_ATTRIBUTE = 'data-emoji-index';

const INITIAL_EMOJIS = 100;

const MemoizedEmoji = React.memo(EmojiComponent);

interface EmojiCategoryProps {
  title: string;
  startIndex: number;
  activeItemIndex: number | null;
  items: { emoji: { name: string; char: string }; tonedChar: string }[];
  onPick: (emoji: string) => void;
  onRemove?: (emoji: string) => void;
  onItemMouseEnter: (index: number) => void;
  onItemMouseLeave: () => void;
  selectedEmojis: Set<string>;
  disabled?: boolean;
}

const EmojiCategory = forwardRef<HTMLDivElement, EmojiCategoryProps>(
  (
    {
      title,
      startIndex,
      activeItemIndex,
      items,
      onPick,
      onRemove,
      onItemMouseEnter,
      onItemMouseLeave,
      selectedEmojis,
      disabled,
    },
    ref,
  ) => (
    <div ref={ref} className={styles.category}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.emojiList}>
        {items.map(({ emoji, tonedChar }, itemIndex) => {
          const index = itemIndex + startIndex;
          const isSelected = selectedEmojis.has(tonedChar);
          return (
            <button
              key={tonedChar}
              type="button"
              title={emoji.name}
              {...addTestId(`emoji_${emoji.name}`)}
              className={classNames(styles.emoji, {
                [styles.isActive]: index === activeItemIndex,
                [styles.selected]: isSelected,
              })}
              onClick={() =>
                isSelected ? onRemove?.(tonedChar) : onPick(tonedChar)
              }
              onMouseEnter={() => onItemMouseEnter(index)}
              onMouseLeave={() => onItemMouseLeave()}
              {...{ [EMOJI_INDEX_ATTRIBUTE]: index }}
              disabled={disabled}
            >
              <MemoizedEmoji char={tonedChar} size="large" />
            </button>
          );
        })}
      </div>
    </div>
  ),
);

interface CategoryTabButtonProps {
  icon: string;
  selectedCategoryId: CATEGORY;
  categoryId: CATEGORY;
  onClick: (categoryId: CATEGORY) => void;
  'aria-label': string;
}

const CategoryTabButton = forwardRef<HTMLButtonElement, CategoryTabButtonProps>(
  (
    { icon, selectedCategoryId, categoryId, onClick, 'aria-label': ariaLabel },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      role="tab"
      aria-label={ariaLabel}
      aria-selected={selectedCategoryId === categoryId}
      onClick={() => onClick(categoryId)}
      className={styles.tabButton}
    >
      <SVGIcon>{icon}</SVGIcon>
    </button>
  ),
);

enum CATEGORY {
  Home,
  SmileysPeople,
  AnimalsNature,
  FoodDrinks,
  Activities,
  TravelPlaces,
  ObjectSymbols,
  Flags,
}

const CATEGORY_MAP: Record<EmojiCategoryType, CATEGORY> = {
  people: CATEGORY.SmileysPeople,
  animals_and_nature: CATEGORY.AnimalsNature,
  food_and_drink: CATEGORY.FoodDrinks,
  travel_and_places: CATEGORY.TravelPlaces,
  activity: CATEGORY.Activities,
  objects: CATEGORY.ObjectSymbols,
  symbols: CATEGORY.ObjectSymbols,
  flags: CATEGORY.Flags,
};

const EMOJI_CATEGORIES: Map<
  CATEGORY,
  { title: string; icon: string; emojis: Emoji[] }
> = new Map([
  [
    CATEGORY.SmileysPeople,
    { title: 'Smileys & People', icon: smileysPeopleIcon, emojis: [] },
  ],
  [
    CATEGORY.AnimalsNature,
    { title: 'Animals & Nature', icon: animalsNatureIcon, emojis: [] },
  ],
  [
    CATEGORY.FoodDrinks,
    { title: 'Food & Drink', icon: foodDrinksIcon, emojis: [] },
  ],
  [
    CATEGORY.Activities,
    { title: 'Activities', icon: activitiesIcon, emojis: [] },
  ],
  [
    CATEGORY.TravelPlaces,
    { title: 'Travel & Places', icon: travelPlacesIcon, emojis: [] },
  ],
  [
    CATEGORY.ObjectSymbols,
    { title: 'Objects & Symbols', icon: lightbulbIcon, emojis: [] },
  ],
  [CATEGORY.Flags, { title: 'Flags', icon: flagIcon, emojis: [] }],
]);
emojiList.forEach((emoji) => {
  EMOJI_CATEGORIES.get(CATEGORY_MAP[emoji.category])?.emojis.push(emoji);
});

const toneEmojis = (emojis: Emoji[], modifier: string | null) =>
  emojis.map((emoji) => ({
    emoji,
    tonedChar: applySkinToneToEmoji(emoji, modifier),
  }));

const useIsMouseMoving = () => {
  const isMouseMovingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseMove = () => {
    isMouseMovingRef.current = true;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      isMouseMovingRef.current = false;
      timerRef.current = null;
    }, 500);
  };

  return { isMouseMovingRef, onMouseMove: handleMouseMove };
};

interface EmojiSection {
  id: CATEGORY;
  title: string;
  items: { emoji: Emoji; tonedChar: string }[];
}

interface NavigateOptions {
  sections: EmojiSection[];
  currentIndex: number;
  direction: 'up' | 'down' | 'left' | 'right';
  emojiCountPerRow: number;
}

const navigate = ({
  sections,
  currentIndex,
  direction,
  emojiCountPerRow,
}: NavigateOptions) => {
  switch (direction) {
    case 'left':
      return Math.max(currentIndex - 1, 0);
    case 'right':
      return Math.min(
        currentIndex + 1,
        sections.reduce((acc, s) => acc + s.items.length, 0) - 1,
      );
    default: {
      let currentSectionIndex = 0;
      let currentIndexInSection = currentIndex;
      sections.some((section) => {
        if (currentIndexInSection < section.items.length) return true;

        currentIndexInSection -= section.items.length;
        currentSectionIndex += 1;
        return false;
      });

      const currentSection = sections[currentSectionIndex];
      if (!currentSection) return null;

      const column = currentIndexInSection % emojiCountPerRow;

      if (direction === 'down') {
        const remainingInCurrentSection =
          currentSection.items.length - currentIndexInSection - 1;

        if (emojiCountPerRow < remainingInCurrentSection) {
          return currentIndex + emojiCountPerRow;
        }
        if (column + remainingInCurrentSection > emojiCountPerRow) {
          return currentIndex + remainingInCurrentSection;
        }
        const nextSection = sections[currentSectionIndex + 1];
        if (!nextSection) return currentIndex + remainingInCurrentSection;

        return (
          currentIndex +
          remainingInCurrentSection +
          Math.min(column + 1, nextSection.items.length)
        );
      }

      if (currentIndexInSection >= emojiCountPerRow) {
        return currentIndex - emojiCountPerRow;
      }
      const prevSection = sections[currentSectionIndex - 1];
      if (!prevSection) return 0;

      const countInTrailingLine =
        prevSection.items.length % emojiCountPerRow || emojiCountPerRow;
      if (countInTrailingLine > column) {
        return (
          currentIndex - currentIndexInSection - (countInTrailingLine - column)
        );
      }

      return currentIndex - currentIndexInSection - 1;
    }
  }
};

type EmojiPickerProps = {
  onPick: (emoji: string) => void;
  onRemove?: (emoji: string) => void;
  onEscapePress?: () => void;
  controlsPosition?: 'top' | 'bottom';
  fullWidth?: boolean;
  inline?: boolean;
  disabled?: boolean;
  selectedEmojis?: Set<string>;
  initialEmojisTimeout?: number;
  searchInputRef?: React.Ref<HTMLInputElement>;
  additionalRecentEmojis?: string[];
} & (
  | {
      query: string;
      onClearQuery?: () => void;
      inputContainer: HTMLDivElement | null;
    }
  | {
      query?: never;
      onClearQuery?: never;
      inputContainer?: never;
    }
);

const EmojiPicker = ({
  query: externalQuery,
  onClearQuery,
  inputContainer,
  onPick,
  onRemove,
  onEscapePress,
  controlsPosition = 'top',
  fullWidth,
  inline,
  disabled,
  selectedEmojis = new Set(),
  initialEmojisTimeout,
  searchInputRef,
  additionalRecentEmojis,
}: EmojiPickerProps) => {
  const [modifier, setModifier] = useAtom(emojiSkinToneModifierAtom);
  const [index, setIndex] = useState<number | null>(null);
  const [isInitialRender, setIsInitialRender] =
    useState(!!initialEmojisTimeout);

  useEffect(() => {
    if (!initialEmojisTimeout) return undefined;

    const timeout = setTimeout(() => {
      startTransition(() => {
        setIsInitialRender(false);
      });
    }, initialEmojisTimeout);

    return () => {
      clearTimeout(timeout);
    };
  }, [initialEmojisTimeout]);

  const [internalQuery, setInternalQuery] = useState('');
  const query = externalQuery ?? internalQuery;

  const [previousQuery, setPreviousQuery] = useState(query);
  const [selectedCategoryId, setSelectedCategoryId] = useState<CATEGORY>(
    CATEGORY.Home,
  );
  const [isSelectingSkinTone, setIsSelectingSkinTone] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [emojiCountPerRow, setEmojiCountPerRow] = useState(8);
  const [tabButtonWidth, setTabButtonWidth] = useState(34);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedCategoryIdRef = useRef(selectedCategoryId);
  const tabButtonRef = useRef<HTMLButtonElement>(null);

  const { isMouseMovingRef, onMouseMove } = useIsMouseMoving();

  const isSearchInputShown = typeof externalQuery === 'undefined';

  useLayoutEffect(() => {
    if (tabButtonRef.current) {
      setTabButtonWidth(tabButtonRef.current.getBoundingClientRect().width);
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const resizeObserver = new ResizeObserver(() => {
      const internalWidth = containerRef.current?.clientWidth;
      if (internalWidth) {
        setEmojiCountPerRow(
          Math.floor(
            (internalWidth - EMOJI_LIST_HORIZONTAL_PODDING * 2) / EMOJI_WIDTH,
          ),
        );
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    selectedCategoryIdRef.current = selectedCategoryId;
  });

  const handleChangeActiveEmoji = useCallback((i: typeof index) => {
    setIndex(i);
  }, []);

  useEffect(() => {
    if (query !== previousQuery) {
      setPreviousQuery(query);
      if (query && !previousQuery) {
        setSelectedCategoryId(CATEGORY.Home);
      }
      handleChangeActiveEmoji(0);
    }
  }, [query, previousQuery, handleChangeActiveEmoji]);

  const scrollAreaRef = useRef<OverlayScrollbarsComponentRef | null>(null);
  const categoryNodesRef = useRef<Map<CATEGORY, HTMLDivElement | null>>(
    new Map(),
  );

  const scrollToCategory = (categoryId: CATEGORY) => {
    const node = categoryNodesRef.current.get(categoryId);
    if (node) {
      const viewport = scrollAreaRef.current?.osInstance()?.elements().viewport;
      if (viewport) {
        viewport.scrollTop = node.offsetTop;
      }
    }
  };

  const onTabButtonClick = (categoryId: CATEGORY) => {
    setSelectedCategoryId(categoryId);
    scrollToCategory(categoryId);

    if (categoryId !== CATEGORY.Home) onClearQuery?.();
  };

  useEffect(() => {
    scrollToCategory(query ? CATEGORY.Home : selectedCategoryIdRef.current);
  }, [query]);

  const searchResultSection = useMemo<EmojiSection | null>(() => {
    if (!query) return null;
    const items = searchEmoji(query).map((result) => result.item);
    return {
      id: CATEGORY.Home,
      title: items.length ? 'Results' : 'No results',
      items: toneEmojis(items, modifier),
    };
  }, [query, modifier]);

  const defaultSections = useMemo(() => {
    const sections = [...EMOJI_CATEGORIES.entries()].map(
      ([id, { title, emojis }]) => ({
        id,
        title,
        items: toneEmojis(emojis, modifier),
      }),
    );
    if (isInitialRender && sections[0]) {
      return [
        { ...sections[0], items: sections[0].items.slice(0, INITIAL_EMOJIS) },
      ];
    }
    return sections;
  }, [modifier, isInitialRender]);

  const recentlyUsedEmojis = useRecentlyUsedEmojis();

  const recentEmojiItems = _.uniqBy(
    [
      ...toneEmojis(
        _.compact(additionalRecentEmojis?.map(getEmojiItemByChar)),
        modifier,
      ),
      ...toneEmojis(
        _.compact(recentlyUsedEmojis.map(getEmojiItemByName)),
        modifier,
      ),
    ],
    _.property('tonedChar'),
  ).slice(0, 2 * emojiCountPerRow);

  const sections: EmojiSection[] = searchResultSection
    ? [searchResultSection]
    : [
        {
          id: CATEGORY.Home,
          title: 'Recent',
          items: recentEmojiItems,
        },
        ...defaultSections,
      ];

  const startIndexes = sections.reduce(
    (indexes, current) => [
      ...indexes,
      (_.last(indexes) ?? 0) + current.items.length,
    ],
    [0],
  );

  const keyboardHandler = (event: KeyboardEvent | ReactKeyboardEvent) => {
    const map: Record<(typeof event)['key'], NavigateOptions['direction']> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowDown: 'down',
      ArrowUp: 'up',
    };

    const direction = map[event.key];
    if (direction) {
      const nextIndex = navigate({
        sections,
        direction,
        currentIndex: index ?? 0,
        emojiCountPerRow,
      });
      if (typeof nextIndex === 'number') {
        handleChangeActiveEmoji(nextIndex);

        event.preventDefault();

        const node = containerRef.current?.querySelector(
          `[${EMOJI_INDEX_ATTRIBUTE}="${nextIndex}"]`,
        );
        node?.scrollIntoView({ block: 'nearest' });
      }

      return;
    }

    if (event.key === 'Enter' && index != null) {
      const item = sections.map((s) => s.items).flat()[index];
      if (item) {
        onPick(item.tonedChar);
        event.preventDefault();
      }
    }

    if (event.key === 'Escape') {
      if (onEscapePress) {
        onEscapePress();
        event.preventDefault();
      }
    }
  };

  useEventListener(inputContainer, 'keydown', keyboardHandler, {
    options: { capture: true },
  });

  const searchComponent = isSearchInputShown ? (
    <div
      className={classNames(styles.searchPanel, {
        [styles.skinTonePickerOpen]: isSelectingSkinTone,
      })}
    >
      {isSelectingSkinTone ? (
        <>
          {[null, ...skinToneModifiers].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => {
                setModifier(value);
                setIsSelectingSkinTone(false);
              }}
              className={styles.skinToneItem}
            >
              <div className={styles.skinTone} />
            </button>
          ))}
        </>
      ) : (
        <div className={styles.searchField}>
          <FormField
            type="text"
            name="search"
            placeholder="Search..."
            margin="none"
            inputRef={searchInputRef}
            value={internalQuery}
            onFocus={() => {
              handleChangeActiveEmoji(0);
            }}
            onChange={(e) => {
              startTransition(() => {
                setInternalQuery(e.target.value);
              });
            }}
            prepend={<SVGIcon size={16}>{searchIcon}</SVGIcon>}
            {...addTestId('emojiSearch')}
          />
        </div>
      )}
      <IconButton
        aria-label="skin-tone"
        onClick={() => setIsSelectingSkinTone((value) => !value)}
        className={styles.skinToneButton}
      >
        <SVGIcon size={20}>
          {applySkinToneToEmoji(
            { char: '👋', fitzpatrickScale: true },
            modifier,
          )}
        </SVGIcon>
      </IconButton>
    </div>
  ) : null;

  const emojiCategoriesList = [...EMOJI_CATEGORIES.entries()];

  const tabsComponent = (
    <div
      className={classNames(styles.tabList, {
        [styles.hasScrolled]: hasScrolled,
      })}
      role="tablist"
      aria-label="Categories"
    >
      <div
        className={styles.tabIndicator}
        style={{
          left:
            (emojiCategoriesList.findIndex(
              ([id]) => id === selectedCategoryId,
            ) +
              1) *
              tabButtonWidth +
            TAB_LIST_HORIZONTAL_PADDING,
          width: tabButtonWidth,
        }}
      />
      <CategoryTabButton
        ref={tabButtonRef}
        icon={homeReactionIcon}
        categoryId={CATEGORY.Home}
        selectedCategoryId={selectedCategoryId}
        aria-label="Home"
        onClick={onTabButtonClick}
      />
      {emojiCategoriesList.map(([id, { title, icon }]) => (
        <CategoryTabButton
          key={id}
          icon={icon}
          categoryId={id}
          selectedCategoryId={selectedCategoryId}
          aria-label={title}
          onClick={onTabButtonClick}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, {
        [styles.spaceBetween]: controlsPosition === 'bottom',
        [styles.fullWidth]: fullWidth,
        [styles.inline]: inline,
      })}
      aria-label="Emoji picker"
      onKeyDown={keyboardHandler}
      {...addTestId('emojiPicker')}
    >
      {controlsPosition === 'top' && (
        <div
          className={classNames(styles.controls, {
            [styles.hasScrolled]: hasScrolled,
          })}
        >
          {searchComponent}
          {tabsComponent}
        </div>
      )}
      <div
        role="tabpanel"
        className={styles.tabPanel}
        onMouseMove={onMouseMove}
      >
        <ScrollArea
          ref={scrollAreaRef}
          className={styles.scrollArea}
          events={{
            scroll: (event) => {
              const { viewport } = event.elements();
              sections
                .map((s) => s.id)
                .toReversed()
                .some((id) => {
                  const node = categoryNodesRef.current.get(id);
                  if (!node) return false;
                  const top = node.offsetTop - viewport.scrollTop;
                  if (top <= 0) {
                    setSelectedCategoryId(id);
                    return true;
                  }
                  return false;
                });
              setIsScrolledToBottom(
                viewport.scrollHeight -
                  viewport.scrollTop -
                  viewport.clientHeight <
                  1,
              );
              setHasScrolled(viewport.scrollTop > 0);
            },
          }}
        >
          {sections.map((section, sectionIndex) => {
            const startIndex = startIndexes[sectionIndex] ?? 0;

            return (
              <EmojiCategory
                ref={(node) => {
                  categoryNodesRef.current.set(section.id, node);
                }}
                startIndex={startIndex}
                activeItemIndex={index}
                key={section.id}
                title={section.title}
                items={section.items}
                disabled={disabled}
                onPick={onPick}
                onRemove={onRemove}
                selectedEmojis={selectedEmojis}
                onItemMouseEnter={(i) => {
                  // Only update the index if the mouse is moving.
                  // Otherwise, the callback is likely triggered by an item
                  // moving under the cursor due to the keyboard navigation.
                  if (isMouseMovingRef.current) {
                    handleChangeActiveEmoji(i);
                  }
                }}
                onItemMouseLeave={() => {
                  if (isMouseMovingRef.current) {
                    handleChangeActiveEmoji(null);
                  }
                }}
              />
            );
          })}
        </ScrollArea>
      </div>
      {controlsPosition === 'bottom' && (
        <div
          className={classNames(styles.controls, styles.atBottom, {
            [styles.notScrolledToBottom]: !isScrolledToBottom,
            [styles.bottomPadding]: isSearchInputShown,
          })}
        >
          {tabsComponent}
          {searchComponent}
        </div>
      )}
    </div>
  );
};

type EmojiPickerContainerProps = {
  children: React.ReactNode;
  showPopOverMargin?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const EmojiPickerContainer = React.forwardRef<
  HTMLDivElement,
  EmojiPickerContainerProps
>(({ children, ...props }, ref) => (
  <div className={styles.pickerContainer} ref={ref} {...props}>
    {children}
  </div>
));

const POPOVER_OFFSET = {
  regular: 8,
  small: 4,
};

interface Bounds {
  bottom?: number;
  height?: number | string;
  left?: number;
  right?: number;
  top?: number;
  width?: number | string;
}

interface EmojiPickerPopoverProps {
  children: React.ReactNode;
  bounds?: Bounds;
  open: boolean;
  setOpen?: (open: boolean) => void;
  target?: React.ReactNode;
  placement?: Placement;
  responsivePlacement?: boolean;
  offset?: keyof typeof POPOVER_OFFSET;
}

export const EmojiPickerPopOver = ({
  children,
  bounds = {},
  open,
  setOpen,
  target,
  placement = 'bottom-start',
  responsivePlacement,
  offset: offsetKey = 'regular',
}: EmojiPickerPopoverProps) => {
  const isSmall = useIsSize(Sizes.SM);
  const targetRef = useRef<HTMLDivElement>(null);

  const middleware = useMemo(
    () => [
      offset(POPOVER_OFFSET[offsetKey]),
      hide(),
      flip({
        fallbackStrategy: 'initialPlacement',
      }),
    ],
    [offsetKey],
  );

  const { refs, floatingStyles } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
  });

  if (isSmall && responsivePlacement) {
    return (
      <>
        {open &&
          createPortal(
            <>
              <div
                className={classNames(
                  styles.pickerContainer,
                  styles.responsivePopOver,
                )}
              >
                {children}
              </div>
              <div
                className={styles.backdrop}
                onClick={() => setOpen?.(false)}
              />
            </>,
            document.body,
          )}
        {target}
      </>
    );
  }

  return (
    <div className={styles.popOverContainer}>
      {open && (
        <>
          <ClickOutsideHandler
            onClickOutside={(e) => {
              if (
                e.target instanceof HTMLElement &&
                targetRef.current?.contains(e.target)
              ) {
                return;
              }
              setOpen?.(false);
            }}
          >
            <>
              {createPortal(
                <EmojiPickerContainer
                  ref={refs.setFloating}
                  style={floatingStyles}
                  {...addTestId('emojiAutocompleter')}
                >
                  {children}
                </EmojiPickerContainer>,
                document.body,
              )}
            </>
          </ClickOutsideHandler>
          <div
            ref={refs.setReference}
            style={{
              position: 'absolute',
              pointerEvents: 'none',
              left: bounds.left,
              top: bounds.top,
              bottom: bounds.bottom,
              height:
                bounds.height ??
                // Using either top + bottom or 100% height, can't have both
                ('top' in bounds && 'bottom' in bounds ? undefined : '100%'),
              width:
                bounds.width ??
                ('left' in bounds && 'right' in bounds ? undefined : '100%'),
            }}
          />
        </>
      )}
      {!!target && <div ref={targetRef}>{target}</div>}
    </div>
  );
};

export default EmojiPicker;
